import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
// react-icons types are not yet compatible with React 19 typings – cast to any for now
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import {
  FiEdit2 as FiEdit2Raw,
  FiClock as FiClockRaw,
  FiCheckCircle as FiCheckCircleRaw,
} from 'react-icons/fi';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { appConfigService } from '../services/appConfigService';
import { getRoleLabel } from '../utils/roles';
import { useAuth } from '../context/AuthContext';
import { useCurrentUserProfile } from '../hooks/useCurrentUserProfile';
import { ADMIN_HOUSEHOLDS_QUERY_KEY } from './HouseholdsPage';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const FiEdit2: any = FiEdit2Raw;
const FiClock: any = FiClockRaw;
const FiCheckCircle: any = FiCheckCircleRaw;

const Page = styled.div`
  background: var(--background);
  padding: 1.5rem;
  min-height: 100%;
  display: flex;
  flex-direction: column;
  gap: 4rem;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
`;

const HeaderTitle = styled.h1`
  margin: 0;
  font-size: 1.8rem;
  color: var(--onSurface);
`;

const MainCardGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
`;

const DetailAndNotesRow = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: minmax(0, 2fr) minmax(260px, 1fr);
  align-items: stretch;

  @media (max-width: 960px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: var(--surface);
  border: 1px solid var(--outline);
  border-radius: 14px;
  padding: 1.25rem;
  box-shadow: 0 12px 24px rgba(17, 24, 39, 0.06);
  position: relative;
`;

const DetailCard = styled(Card)`
  height: 100%;
`;

const NotesCard = styled(Card)`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const NotesHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.25rem;
`;

const NotesActionButton = styled.button`
  border: none;
  background: var(--primary);
  color: var(--surface);
  border-radius: 10px;
  padding: 0.35rem 0.75rem;
  cursor: pointer;
  font-weight: 600;
  font-size: 0.85rem;
  white-space: nowrap;
  box-shadow: 0 8px 16px rgba(31, 117, 254, 0.25);

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const NotesBody = styled.div`
  flex: 1;
  overflow: auto;
`;

const AvatarShell = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
`;

const AvatarActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const AvatarWrap = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 16px;
  overflow: hidden;
  background: var(--outline);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  font-weight: 700;
  color: var(--onSurfaceVariant);
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const OnlineDot = styled.span`
  position: absolute;
  right: -4px;
  bottom: -4px;
  width: 12px;
  height: 12px;
  border-radius: 999px;
  background: var(--success);
  border: 2px solid var(--surface);
  box-shadow: 0 0 0 1px rgba(34, 197, 94, 0.4);
`;

const MessageLink = styled(Link)`
  border: 1px solid #cbd5f5;
  background: var(--surface);
  color: var(--primary);
  padding: 0.35rem 0.75rem;
  border-radius: 999px;
  font-size: 0.85rem;
  font-weight: 600;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;

  &:hover {
    background: #eef4ff;
  }
`;

const SectionGrid = styled.div`
  display: grid;
  gap: 1rem;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
`;

const Label = styled.p`
  margin: 0;
  color: #718096;
  font-size: 0.85rem;
`;

const Value = styled.p`
  margin: 0 0 0.75rem;
  color: var(--onSurface);
  font-weight: 600;
`;

const Select = styled.select`
  width: 100%;
  max-width: 100%;
  padding: 0.55rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  font-size: 0.9rem;
  box-sizing: border-box;
`;

const TextArea = styled.textarea`
  width: 100%;
  max-width: 100%;
  min-height: 80px;
  padding: 0.6rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  font-size: 0.9rem;
  box-sizing: border-box;
`;

const TextInput = styled.input`
  width: 100%;
  max-width: 100%;
  padding: 0.55rem 0.75rem;
  border-radius: 8px;
  border: 1px solid var(--outline);
  font-size: 0.9rem;
  box-sizing: border-box;
`;

const Button = styled.button`
  background: var(--primary);
  border: none;
  color: var(--surface);
  border-radius: 12px;
  padding: 0.55rem 1rem;
  cursor: pointer;
  font-weight: 600;
  align-self: flex-start;
  box-shadow: 0 10px 18px rgba(31, 117, 254, 0.25);

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const TagPill = styled.span`
  display: inline-flex;
  align-items: center;
  background: #eef4ff;
  color: var(--primary);
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 600;
  margin: 0 6px 6px 0;
`;

const HouseholdMemberList = styled.ul`
  list-style: none;
  margin: 0 0 0.75rem;
  padding: 0;
  display: grid;
  gap: 0.5rem;
`;

const HouseholdMemberItem = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.5rem 0.6rem;
  border-radius: 10px;
  background: var(--surfaceVariant);
  border: 1px solid var(--outline);
`;

const HouseholdMemberName = styled.span`
  font-weight: 600;
  color: var(--onSurface);
`;

const HouseholdMemberLink = styled(Link)`
  font-weight: 600;
  color: var(--onSurface);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const HouseholdMemberMeta = styled.span`
  font-size: 0.85rem;
  color: var(--onSurfaceVariant);
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.5rem;
  z-index: 200;
`;

const ModalCard = styled(Card)`
  width: min(720px, 100%);
  max-height: 90vh;
  overflow: auto;
  overflow-x: hidden;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const CloseButton = styled.button`
  border: none;
  background: transparent;
  color: var(--onSurfaceVariant);
  font-size: 1.1rem;
  cursor: pointer;
`;

const ModalSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const ImageModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 240;
  padding: 2rem;
`;

const ImageModalContent = styled.div`
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const FullImage = styled.img`
  max-width: 90vw;
  max-height: 90vh;
  border-radius: 16px;
  box-shadow: 0 20px 40px rgba(15, 23, 42, 0.45);
`;

const ToastRegion = styled.div`
  position: fixed;
  top: 5rem;
  right: 1.25rem;
  z-index: 220;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  align-items: flex-end;
  pointer-events: none;
`;

const Toast = styled.div<{ $variant: 'success' | 'error' | 'info' }>`
  padding: 0.9rem 1.1rem;
  border-radius: 10px;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.18);
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--onSurface);
  background: ${({ $variant }) =>
    $variant === 'success' ? '#dcfce7' : $variant === 'error' ? '#fee2e2' : '#dbeafe'};
  border: 1px solid
    ${({ $variant }) =>
      $variant === 'success' ? '#86efac' : $variant === 'error' ? '#fecaca' : '#93c5fd'};
  pointer-events: auto;
  max-width: 360px;
`;

const ToastTimestamp = styled.div`
  font-size: 0.75rem;
  font-weight: 500;
  color: var(--onSurfaceVariant);
  margin-top: 0.25rem;
`;
const AccordionToggle = styled.button<{ $open?: boolean }>`
  border: 1px solid var(--outline);
  background: ${({ $open }) => ($open ? '#f1f5ff' : 'var(--surface)')};
  color: #1f2937;
  border-radius: 10px;
  padding: 0.6rem 0.8rem;
  font-weight: 600;
  cursor: pointer;
  text-align: left;
`;

const FieldRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 0.75rem;
  align-items: end;
`;

const DropdownList = styled.ul`
  list-style: none;
  margin: 0.35rem 0 0;
  padding: 0;
  border: 1px solid var(--outline);
  border-radius: 8px;
  max-height: 180px;
  overflow: auto;
`;

const DropdownItem = styled.li`
  padding: 0.55rem 0.75rem;
  cursor: pointer;
  color: #1f2937;

  &:hover {
    background: #f1f5ff;
  }
`;

const EditActionRow = styled.div`
  position: absolute;
  top: 12px;
  right: 12px;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
`;

const EditButton = styled.button`
  border: none;
  background: #eef4ff;
  color: var(--primary);
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:hover {
    background: #dbe7ff;
  }
`;

const VerifyInfoButton = styled.button<{ $verified?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: ${({ $verified }) => ($verified ? '#dcfce7' : '#fef3c7')};
  color: ${({ $verified }) => ($verified ? '#166534' : '#92400e')};
  border-radius: 999px;
  padding: 4px 10px;
  font-size: 0.75rem;
  font-weight: 700;
  border: none;
  cursor: pointer;

  &:disabled {
    cursor: default;
    opacity: 0.7;
  }
`;

const HistoryButton = styled.button`
  border: none;
  background: #eef4ff;
  color: var(--primary);
  width: 32px;
  height: 32px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const SectionTitle = styled.h2`
  margin: 0 0 0.5rem;
  font-size: 1.2rem;
  color: var(--onSurface);
`;

const CheckboxRow = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: var(--onSurfaceVariant);
  margin-top: 0.5rem;
`;

const BackLink = styled(Link)`
  color: var(--primary);
  text-decoration: none;
  font-weight: 600;

  &:hover {
    text-decoration: underline;
  }
`;

interface MemberProfile {
  id: string;
  full_name: string;
  email?: string | null;
  phone?: string | null;
  title?: string | null;
  avatar?: string | null;
  membership_status?: string | null;
  campus?: string | null;
  member_since?: string | null;
  dob?: string | null;
  baptism_date?: string | null;
  roles?: string[] | null;
  inactive_reason?: string | null;
  last_active_at?: string | null;
}

interface MemberNote {
  id: string;
  note: string;
  created_at: string;
  is_private: boolean;
}

interface StatusHistory {
  id: string;
  status: string;
  created_at: string;
  reason?: string | null;
}

interface CampusVisitCount {
  campus: string;
  visit_count: number;
  last_visit_at?: string | null;
  first_visit_at?: string | null;
}

interface VolunteerCategory {
  key: string;
  label?: string | null;
}

interface MemberMinistryAssignment {
  id: string;
  category_key: string;
  volunteer_categories?: { key?: string | null; label?: string | null } | null;
}

interface Household {
  id: string;
  name: string;
  address?: string | null;
  phone?: string | null;
  primary_contact_id?: string | null;
}

interface HouseholdMember {
  id: string;
  user_id: string;
  household_id?: string | null;
  relationship?: string | null;
  is_primary?: boolean | null;
  married_date?: string | null;
}

interface HouseholdMemberProfile {
  id: string;
  full_name: string;
  email?: string | null;
  phone?: string | null;
  avatar?: string | null;
}

interface HouseholdMemberWithProfile extends HouseholdMember {
  user?: HouseholdMemberProfile | null;
}

interface HouseholdMemberWithProfileRaw extends HouseholdMember {
  user?: HouseholdMemberProfile | HouseholdMemberProfile[] | null;
}

const STATUS_OPTIONS = [
  { value: 'visitor', label: 'Visitor' },
  { value: 'partner', label: 'Partner' },
  { value: 'member', label: 'Member' },
  { value: 'inactive', label: 'Inactive' },
];

const CAMPUS_OPTIONS = [
  { value: '', label: 'No campus selected' },
  { value: 'Main Campus', label: 'Main Campus' },
  { value: 'Tampa Campus', label: 'Tampa Campus' },
];

const ONLINE_WINDOW_MS = 5 * 60 * 1000;

const MemberDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { data: currentUserProfile } = useCurrentUserProfile(user?.id);
  const [profile, setProfile] = useState<MemberProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notes, setNotes] = useState<MemberNote[]>([]);
  const [ministries, setMinistries] = useState<string[]>([]);
  const [volunteerCategories, setVolunteerCategories] = useState<VolunteerCategory[]>([]);
  const [ministryKeys, setMinistryKeys] = useState<string[]>([]);
  const [pendingMinistryKeys, setPendingMinistryKeys] = useState<string[]>([]);
  const [savingMinistries, setSavingMinistries] = useState(false);
  const [ministriesError, setMinistriesError] = useState<string | null>(null);
  const [statusHistory, setStatusHistory] = useState<StatusHistory[]>([]);
  const [status, setStatus] = useState('visitor');
  const [inactiveReason, setInactiveReason] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [campusVisits, setCampusVisits] = useState<CampusVisitCount[]>([]);
  const [campusVisitError, setCampusVisitError] = useState<string | null>(null);
  const [showVisitHistory, setShowVisitHistory] = useState(false);
  const [showAddNote, setShowAddNote] = useState(false);
  const [visitGapDays, setVisitGapDays] = useState(60);
  const [noteText, setNoteText] = useState('');
  const [notePrivate, setNotePrivate] = useState(false);
  const [savingNote, setSavingNote] = useState(false);
  const [noteError, setNoteError] = useState<string | null>(null);
  const [editForm, setEditForm] = useState({
    full_name: '',
    email: '',
    phone: '',
    title: '',
    campus: 'Main Campus',
    member_since: '',
    dob: '',
    baptism_date: '',
  });
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [showEditProfile, setShowEditProfile] = useState(false);
  const [household, setHousehold] = useState<Household | null>(null);
  const [householdMember, setHouseholdMember] = useState<HouseholdMember | null>(null);
  const [households, setHouseholds] = useState<Household[]>([]);
  const [householdMembers, setHouseholdMembers] = useState<HouseholdMemberWithProfile[]>([]);
  const [householdSearch, setHouseholdSearch] = useState('');
  const [selectedHouseholdId, setSelectedHouseholdId] = useState<string | null>(null);
  const [showCreateHousehold, setShowCreateHousehold] = useState(false);
  const [newHouseholdName, setNewHouseholdName] = useState('');
  const [householdForm, setHouseholdForm] = useState({
    name: '',
    address: '',
    relationship: '',
    married_date: '',
    is_primary: false,
  });
  const [savingHousehold, setSavingHousehold] = useState(false);
  const [householdError, setHouseholdError] = useState<string | null>(null);
  const [householdAccordionOpen, setHouseholdAccordionOpen] = useState(false);
  const [ministriesAccordionOpen, setMinistriesAccordionOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [partnerRequestId, setPartnerRequestId] = useState<string | null>(null);
  const [partnerVerified, setPartnerVerified] = useState(false);
  const [partnerVerifySaving, setPartnerVerifySaving] = useState(false);
  const [toast, setToast] = useState<{
    message: string;
    variant: 'success' | 'error' | 'info';
    timestamp: string;
  } | null>(null);
  const toastTimerRef = React.useRef<number | null>(null);

  const statusLabel = useMemo(
    () => STATUS_OPTIONS.find((option) => option.value === status)?.label ?? status,
    [status],
  );
  const isNewPartner = Boolean(partnerRequestId);

  const showToast = React.useCallback(
    (message: string, variant: 'success' | 'error' | 'info' = 'success') => {
      setToast({ message, variant, timestamp: new Date().toLocaleTimeString() });
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
      }
      toastTimerRef.current = window.setTimeout(() => {
        setToast(null);
      }, 4000);
    },
    [],
  );

  const handleVerifyInfo = React.useCallback(async () => {
    if (!partnerRequestId || partnerVerified || partnerVerifySaving) {
      return;
    }
    setPartnerVerifySaving(true);
    const { data, error: verifyError } = await supabase
      .from('partner_requests')
      .update({ verified: true })
      .eq('id', partnerRequestId)
      .select('id, verified');
    if (verifyError) {
      showToast('Failed to verify info.', 'error');
      setPartnerVerifySaving(false);
      return;
    }
    const updated = data?.[0] as { id: string; verified?: boolean | null } | undefined;
    if (!updated) {
      showToast('Unable to confirm verification update.', 'error');
      setPartnerVerifySaving(false);
      return;
    }
    setPartnerVerified(Boolean(updated.verified));
    setPartnerVerifySaving(false);
    showToast('Info verified.');
    try {
      await supabase.from('user_edit_logs').insert({
        source_table: 'partner_requests',
        action: 'verify',
        entity_id: partnerRequestId,
        user_id: id,
        actor_id: user?.id ?? null,
        actor_name: currentUserProfile?.full_name ?? user?.user_metadata?.full_name ?? null,
        actor_email: user?.email ?? null,
        actor_roles: currentUserProfile?.roles ?? user?.user_metadata?.roles ?? null,
        changes: {
          verified: {
            old: false,
            new: true,
          },
        },
      });
    } catch (logError) {
      console.warn('Failed to record verification edit log:', logError);
    }
  }, [partnerRequestId, partnerVerified, partnerVerifySaving, showToast, id, user, currentUserProfile]);

  React.useEffect(() => {
    return () => {
      if (toastTimerRef.current) {
        window.clearTimeout(toastTimerRef.current);
      }
    };
  }, []);

  React.useEffect(() => {
    const loadPartnerRequest = async () => {
      if (!id) {
        setPartnerRequestId(null);
        setPartnerVerified(false);
        return;
      }
      const { data, error: partnerError } = await supabase
        .from('partner_requests')
        .select('id, verified')
        .eq('user_id', id)
        .order('created_at', { ascending: false })
        .limit(1);
      if (partnerError) {
        setPartnerRequestId(null);
        setPartnerVerified(false);
        return;
      }
      const record = (data?.[0] as { id: string; verified?: boolean | null } | undefined) ?? null;
      setPartnerRequestId(record?.id ?? null);
      setPartnerVerified(Boolean(record?.verified));
    };

    loadPartnerRequest();
  }, [id]);

  const filteredHouseholds = useMemo(() => {
    const search = householdSearch.trim().toLowerCase();
    if (!search) return [];
    return households.filter((item) => item.name.toLowerCase().includes(search));
  }, [householdSearch, households]);

  const sortedCampusVisits = useMemo(() => {
    return [...campusVisits].sort((a, b) => {
      const aTime = a.last_visit_at ? new Date(a.last_visit_at).getTime() : 0;
      const bTime = b.last_visit_at ? new Date(b.last_visit_at).getTime() : 0;
      return bTime - aTime;
    });
  }, [campusVisits]);

  const latestCampusVisit = sortedCampusVisits[0] ?? null;
  const campusVisitHistory = sortedCampusVisits.slice(0, 10);

  const visitGapMessage = useMemo(() => {
    if (!latestCampusVisit?.last_visit_at) return null;
    const lastVisitMs = new Date(latestCampusVisit.last_visit_at).getTime();
    if (!Number.isFinite(lastVisitMs)) return null;
    const gapDays = Number.isFinite(visitGapDays) && visitGapDays > 0 ? visitGapDays : 60;
    const thresholdMs = gapDays * 24 * 60 * 60 * 1000;
    if (Date.now() - lastVisitMs <= thresholdMs) return null;
    return `User hasn't visited since ${new Date(lastVisitMs).toLocaleDateString()}.`;
  }, [latestCampusVisit?.last_visit_at, visitGapDays]);

  const formatDateValue = (dateString?: string | null) => {
    if (!dateString) return '-';
    if (/^\d{4}-\d{2}-\d{2}/.test(dateString)) {
      const [year, month, day] = dateString.split('-').map(Number);
      const utcDate = new Date(Date.UTC(year, month - 1, day));
      return utcDate.toLocaleDateString('en-US', { timeZone: 'UTC' });
    }
    const parsed = new Date(dateString);
    if (Number.isNaN(parsed.getTime())) return '-';
    return parsed.toLocaleDateString();
  };

  useEffect(() => {
    const loadHouseholds = async () => {
      const PAGE = 1000;
      const all: Household[] = [];
      let from = 0;
      while (true) {
        const to = from + PAGE - 1;
        const { data: chunk } = await supabase
          .from('households')
          .select('id, name, address, phone, primary_contact_id')
          .order('name', { ascending: true })
          .range(from, to);
        const rows = (chunk as Household[]) ?? [];
        all.push(...rows);
        if (rows.length < PAGE) break;
        from += PAGE;
      }
      setHouseholds(all);
    };

    const loadProfile = async () => {
      if (!id) return;
      setLoading(true);
      setError(null);
      const [profileRes, notesRes, historyRes, householdMemberRes, categoriesRes, ministriesRes, visitsRes] = await Promise.all([
        supabase
          .from('users')
          .select(
            'id, full_name, email, phone, title, avatar, membership_status, campus, member_since, dob, baptism_date, roles, inactive_reason, last_active_at',
          )
          .eq('id', id)
          .single(),
        supabase
          .from('member_notes')
          .select('id, note, created_at, is_private')
          .eq('member_id', id)
          .order('created_at', { ascending: false }),
        supabase
          .from('membership_status_history')
          .select('id, status, created_at, reason')
          .eq('member_id', id)
          .order('created_at', { ascending: false }),
        supabase
          .from('household_members')
          .select('id, user_id, relationship, married_date, is_primary, household_id')
          .eq('user_id', id)
          .order('is_primary', { ascending: false })
          .order('joined_at', { ascending: true })
          .limit(1)
          .maybeSingle(),
        supabase
          .from('volunteer_categories')
          .select('key, label')
          .order('label', { ascending: true }),
        supabase
          .from('member_ministry_assignments')
          .select('id, category_key, volunteer_categories(key, label)')
          .eq('member_id', id),
        supabase
          .from('campus_visit_counts')
          .select('campus, visit_count, last_visit_at, first_visit_at')
          .eq('user_id', id)
          .order('last_visit_at', { ascending: false }),
      ]);

      if (profileRes.error) {
        setError('Failed to load member profile.');
        setLoading(false);
        return;
      }

      const memberProfile = profileRes.data as MemberProfile;
      setProfile(memberProfile);
      setNotes((notesRes.data as MemberNote[]) || []);
      setStatusHistory((historyRes.data as StatusHistory[]) || []);
      setStatus(memberProfile.membership_status ?? 'visitor');
      setInactiveReason(memberProfile.inactive_reason ?? '');
      if (visitsRes.error) {
        setCampusVisitError('Failed to load campus visit counts.');
        setCampusVisits([]);
      } else {
        setCampusVisitError(null);
        setCampusVisits((visitsRes.data as CampusVisitCount[]) || []);
      }

      const gapResult = await appConfigService.getConfiguration<number>('campus_visit_gap_days');
      if (gapResult.success && gapResult.data != null) {
        const parsed = Number(gapResult.data);
        setVisitGapDays(Number.isFinite(parsed) && parsed > 0 ? parsed : 60);
      } else {
        setVisitGapDays(60);
      }
      const resolvedCampus =
        typeof memberProfile.campus === 'string' && memberProfile.campus.trim()
          ? memberProfile.campus
          : 'Main Campus';
      setEditForm({
        full_name: memberProfile.full_name ?? '',
        email: memberProfile.email ?? '',
        phone: memberProfile.phone ?? '',
        title: memberProfile.title ?? '',
        campus: resolvedCampus,
        member_since: memberProfile.member_since ?? '',
        dob: memberProfile.dob ?? '',
        baptism_date: memberProfile.baptism_date ?? '',
      });
      if (householdMemberRes.data?.household_id) {
        const { data: householdData } = await supabase
          .from('households')
          .select('id, name, address, phone, primary_contact_id')
          .eq('id', householdMemberRes.data.household_id)
          .single();
        setHousehold(householdData as Household);
        setHouseholdMember(householdMemberRes.data as HouseholdMember);
        setSelectedHouseholdId(householdData?.id ?? null);
        setHouseholdSearch(householdData?.name ?? '');
        setHouseholdForm({
          name: householdData?.name ?? '',
          address: householdData?.address ?? '',
          relationship: householdMemberRes.data.relationship ?? '',
          married_date: householdMemberRes.data.married_date ?? '',
          is_primary: Boolean(householdMemberRes.data.is_primary),
        });
      } else {
        setHousehold(null);
        setHouseholdMember(null);
        setSelectedHouseholdId(null);
        setHouseholdSearch('');
        setHouseholdForm({
          name: '',
          address: '',
          relationship: '',
          married_date: '',
          is_primary: false,
        });
      }

      const categoriesList = (categoriesRes.data as VolunteerCategory[]) ?? [];
      setVolunteerCategories(categoriesList);
      const categoryLabelMap = new Map<string, string>(
        categoriesList.map((category) => [String(category.key), String(category.label ?? category.key)]),
      );

      const assignments = (ministriesRes.data as MemberMinistryAssignment[]) ?? [];
      const keys = assignments.map((assignment) => assignment.category_key).filter(Boolean);
      const labels = keys
        .map((key) => categoryLabelMap.get(String(key)) ?? String(key))
        .sort((a, b) => a.localeCompare(b));
      setMinistries(labels);
      setMinistryKeys(keys);
      setPendingMinistryKeys(keys);
      setLoading(false);
    };

    loadHouseholds();
    loadProfile();
  }, [id]);

  useEffect(() => {
    const loadHouseholdMembers = async () => {
      if (!household?.id) {
        setHouseholdMembers([]);
        return;
      }

      const { data } = await supabase
        .from('household_members')
        .select('id, user_id, relationship, is_primary, user:users(id, full_name, email, phone, avatar)')
        .eq('household_id', household.id)
        .order('is_primary', { ascending: false })
        .order('id', { ascending: true });

      const normalized = ((data as HouseholdMemberWithProfileRaw[]) || []).map((row) => ({
        ...row,
        user: Array.isArray(row.user) ? row.user[0] ?? null : row.user ?? null,
      }));
      setHouseholdMembers(normalized);
    };

    loadHouseholdMembers();
  }, [household?.id]);

  const handleSaveStatus = async () => {
    if (!profile) return;
    setSavingStatus(true);
    const { data: authData } = await supabase.auth.getUser();
    const changedBy = authData?.user?.id ?? null;
    const nextReason = status === 'inactive' ? inactiveReason : null;

    const { error: updateError } = await supabase
      .from('users')
      .update({ membership_status: status, inactive_reason: nextReason })
      .eq('id', profile.id);

    if (!updateError) {
      await supabase.from('membership_status_history').insert({
        member_id: profile.id,
        status,
        changed_by: changedBy,
        reason: nextReason,
      });
      setProfile({ ...profile, membership_status: status, inactive_reason: nextReason });
      const { data: historyData } = await supabase
        .from('membership_status_history')
        .select('id, status, created_at, reason')
        .eq('member_id', profile.id)
        .order('created_at', { ascending: false });
      setStatusHistory((historyData as StatusHistory[]) || []);
    }

    if (updateError) {
      setError('Failed to update member status.');
    }
    setSavingStatus(false);
  };

  const handleAddNote = async () => {
    if (!profile || !noteText.trim()) return;
    setSavingNote(true);
    setNoteError(null);
    const { data: authData } = await supabase.auth.getUser();
    const authorId = authData?.user?.id ?? null;
    if (!authorId) {
      setNoteError('Unable to identify your account.');
      setSavingNote(false);
      return;
    }

    const { error: insertError } = await supabase.from('member_notes').insert({
      member_id: profile.id,
      author_id: authorId,
      note: noteText.trim(),
      is_private: notePrivate,
    });

    if (insertError) {
      setNoteError('Failed to add note.');
      setSavingNote(false);
      return;
    }

    const { data: refreshedNotes } = await supabase
      .from('member_notes')
      .select('id, note, created_at, is_private')
      .eq('member_id', profile.id)
      .order('created_at', { ascending: false });

    setNotes((refreshedNotes as MemberNote[]) || []);
    setNoteText('');
    setNotePrivate(false);
    setSavingNote(false);
  };

  const handleSaveProfile = async () => {
    if (!profile) return;
    setSavingProfile(true);
    setProfileError(null);
    const updates = {
      full_name: editForm.full_name.trim(),
      email: editForm.email.trim() || null,
      phone: editForm.phone.trim() || null,
      title: editForm.title.trim() || null,
      campus: editForm.campus.trim() || null,
      member_since: editForm.member_since || null,
      dob: editForm.dob || null,
      baptism_date: editForm.baptism_date || null,
    };

    const { error: updateError } = await supabase
      .from('users')
      .update(updates)
      .eq('id', profile.id);

    if (updateError) {
      setProfileError('Failed to update member profile.');
      showToast('Failed to update member profile.', 'error');
      setSavingProfile(false);
      return;
    }

    setProfile({ ...profile, ...updates });
    setSavingProfile(false);
    showToast('Profile updated.', 'success');
  };

  const handleSaveHousehold = async () => {
    if (!profile) return;
    setSavingHousehold(true);
    setHouseholdError(null);

    let householdId = selectedHouseholdId ?? household?.id ?? null;
    if (!householdId && householdForm.name.trim()) {
      const { data: createdHousehold, error: createError } = await supabase
        .from('households')
        .insert({
          name: householdForm.name.trim(),
          address: householdForm.address.trim() || null,
          primary_contact_id: profile.id,
        })
        .select('id, name, address, phone, primary_contact_id')
        .single();

      if (createError) {
        setHouseholdError('Failed to create household.');
        setSavingHousehold(false);
        return;
      }

      householdId = createdHousehold.id;
      setHousehold(createdHousehold as Household);
      await queryClient.invalidateQueries({ queryKey: ADMIN_HOUSEHOLDS_QUERY_KEY });
    } else if (householdId) {
      const { error: updateError } = await supabase
        .from('households')
        .update({
          name: householdForm.name.trim(),
          address: householdForm.address.trim() || null,
          primary_contact_id: householdForm.is_primary ? profile.id : household?.primary_contact_id ?? null,
        })
        .eq('id', householdId);

      if (updateError) {
        setHouseholdError('Failed to update household.');
        setSavingHousehold(false);
        return;
      }
      setHousehold({
        ...(household as Household),
        name: householdForm.name.trim(),
        address: householdForm.address.trim() || null,
        primary_contact_id: householdForm.is_primary ? profile.id : household?.primary_contact_id ?? null,
      });
      await queryClient.invalidateQueries({ queryKey: ADMIN_HOUSEHOLDS_QUERY_KEY });
    }

    if (householdId) {
      if (selectedHouseholdId && selectedHouseholdId !== household?.id) {
        const selected = households.find((item) => item.id === selectedHouseholdId) ?? null;
        setHousehold(selected);
      }
      if (householdMember) {
        const { data: updatedMember, error: memberUpdateError } = await supabase
          .from('household_members')
          .update({
            household_id: householdId,
            relationship: householdForm.relationship.trim() || null,
            married_date: householdForm.married_date || null,
            is_primary: householdForm.is_primary,
          })
          .eq('id', householdMember.id)
          .select('id, user_id, household_id, relationship, married_date, is_primary')
          .single();

        if (memberUpdateError) {
          setHouseholdError('Failed to update household member.');
          showToast('Failed to update household member.', 'error');
          setSavingHousehold(false);
          return;
        }
        if (updatedMember) {
          setHouseholdMember(updatedMember as HouseholdMember);
        } else {
          setHouseholdMember({
            ...householdMember,
            household_id: householdId,
            relationship: householdForm.relationship.trim() || null,
            married_date: householdForm.married_date || null,
            is_primary: householdForm.is_primary,
          });
        }
      } else {
        const { data: newMember, error: memberInsertError } = await supabase
          .from('household_members')
          .insert({
            household_id: householdId,
            user_id: profile.id,
            relationship: householdForm.relationship.trim() || null,
            married_date: householdForm.married_date || null,
            is_primary: householdForm.is_primary,
          })
          .select('id, user_id, household_id, relationship, married_date, is_primary')
          .single();

        if (memberInsertError) {
          setHouseholdError('Failed to add household member.');
          showToast('Failed to add household member.', 'error');
          setSavingHousehold(false);
          return;
        }
        if (newMember) {
          setHouseholdMember(newMember as HouseholdMember);
        }
      }
    }

    setSavingHousehold(false);
    showToast('Household saved.', 'success');
  };

  const handleClearHousehold = async () => {
    if (!profile) return;
    if (!householdMember?.id) return;
    const confirmed = window.confirm('Remove this member from their household?');
    if (!confirmed) return;
    setSavingHousehold(true);
    setHouseholdError(null);
    const { error: removeError } = await supabase
      .from('household_members')
      .delete()
      .eq('id', householdMember.id);
    if (removeError) {
      setHouseholdError('Failed to remove household member.');
      showToast('Failed to remove household member.', 'error');
      setSavingHousehold(false);
      return;
    }
    setHousehold(null);
    setHouseholdMember(null);
    setSelectedHouseholdId(null);
    setHouseholdSearch('');
    setHouseholdForm({
      name: '',
      address: '',
      relationship: '',
      married_date: '',
      is_primary: false,
    });
    setHouseholdMembers([]);
    setSavingHousehold(false);
    showToast('Household cleared.', 'success');
  };

  const toggleMinistryKey = (key: string) => {
    setPendingMinistryKeys((prev) =>
      prev.includes(key) ? prev.filter((item) => item !== key) : [...prev, key],
    );
  };

  const handleSaveMinistries = async () => {
    if (!profile) return;
    setSavingMinistries(true);
    setMinistriesError(null);
    const { data: authData } = await supabase.auth.getUser();
    const assignedBy = authData?.user?.id ?? null;

    const toRemove = ministryKeys.filter((key) => !pendingMinistryKeys.includes(key));
    const toAdd = pendingMinistryKeys.filter((key) => !ministryKeys.includes(key));

    if (toRemove.length) {
      const { error: removeError } = await supabase
        .from('member_ministry_assignments')
        .delete()
        .eq('member_id', profile.id)
        .in('category_key', toRemove);
      if (removeError) {
        setMinistriesError('Failed to remove ministries.');
        showToast('Failed to remove ministries.', 'error');
        setSavingMinistries(false);
        return;
      }
    }

    if (toAdd.length) {
      const { error: addError } = await supabase
        .from('member_ministry_assignments')
        .insert(
          toAdd.map((key) => ({
            member_id: profile.id,
            category_key: key,
            assigned_by: assignedBy,
          })),
        );
      if (addError) {
        setMinistriesError('Failed to add ministries.');
        showToast('Failed to add ministries.', 'error');
        setSavingMinistries(false);
        return;
      }
    }

    const categoryLabelMap = new Map<string, string>(
      volunteerCategories.map((category) => [String(category.key), String(category.label ?? category.key)]),
    );
    const labels = pendingMinistryKeys
      .map((key) => categoryLabelMap.get(String(key)) ?? String(key))
      .sort((a, b) => a.localeCompare(b));

    setMinistryKeys(pendingMinistryKeys);
    setMinistries(labels);
    setSavingMinistries(false);
    showToast('Ministries updated.', 'success');
  };

  const handleHouseholdSearchChange = (value: string) => {
    setHouseholdSearch(value);
    setSelectedHouseholdId(null);
    setHousehold(null);
    setHouseholdForm((prev) => ({
      ...prev,
      name: value,
    }));
  };

  const handleSelectHousehold = (selected: Household) => {
    setSelectedHouseholdId(selected.id);
    setHousehold(selected);
    setHouseholdSearch(selected.name);
    setHouseholdForm((prev) => ({
      ...prev,
      name: selected.name ?? '',
      address: selected.address ?? '',
    }));
  };

  const handleCreateHousehold = async (name: string) => {
    if (!profile || !name.trim()) return;
    setSavingHousehold(true);
    setHouseholdError(null);

    const { data: createdHousehold, error: createError } = await supabase
      .from('households')
      .insert({
        name: name.trim(),
        address: householdForm.address.trim() || null,
        primary_contact_id: profile.id,
      })
      .select('id, name, address, phone, primary_contact_id')
      .single();

    if (createError) {
      setHouseholdError('Failed to create household.');
      setSavingHousehold(false);
      return;
    }

    setHouseholds((prev) => [createdHousehold as Household, ...prev]);
    handleSelectHousehold(createdHousehold as Household);
    setShowCreateHousehold(false);
    setNewHouseholdName('');
    setSavingHousehold(false);
    await queryClient.invalidateQueries({ queryKey: ADMIN_HOUSEHOLDS_QUERY_KEY });
  };

  const isProfileOnline = useMemo(() => {
    if (!profile?.last_active_at) return false;
    const lastActiveMs = new Date(profile.last_active_at).getTime();
    if (!Number.isFinite(lastActiveMs)) return false;
    return Date.now() - lastActiveMs <= ONLINE_WINDOW_MS;
  }, [profile?.last_active_at]);

  return (
    <Page>
      <HeaderRow>
        <HeaderTitle>Member Detail</HeaderTitle>
        <BackLink to="/admin/users">Back to Users</BackLink>
      </HeaderRow>
      {loading && <p>Loading member…</p>}
      {error && <p>{error}</p>}
      {!loading && !error && profile && (
        <>
          {toast && (
            <ToastRegion>
              <Toast $variant={toast.variant}>
                {toast.message}
                <ToastTimestamp>{toast.timestamp}</ToastTimestamp>
              </Toast>
            </ToastRegion>
          )}
          <DetailAndNotesRow>
            <DetailCard>
              <EditActionRow>
                {isNewPartner && (
                  <VerifyInfoButton
                    type="button"
                    onClick={handleVerifyInfo}
                    disabled={partnerVerified || partnerVerifySaving}
                    $verified={partnerVerified}
                  >
                    <FiCheckCircle size={14} />
                    {partnerVerified ? 'Info Verified' : 'Verify Info'}
                  </VerifyInfoButton>
                )}
                <EditButton type="button" onClick={() => setShowEditProfile((open) => !open)}>
                  <FiEdit2 size={16} />
                </EditButton>
              </EditActionRow>
              <AvatarActionRow>
                <AvatarShell>
                  <AvatarWrap>
                    {profile.avatar ? (
                      <AvatarImage
                        src={profile.avatar}
                        alt={profile.full_name}
                        onClick={() => setImageModalOpen(true)}
                        style={{ cursor: 'pointer' }}
                      />
                    ) : (
                      (profile.full_name || 'U').charAt(0).toUpperCase()
                    )}
                  </AvatarWrap>
                  {isProfileOnline && <OnlineDot title="Online" />}
                </AvatarShell>
                {user?.id !== profile.id && (
                  <MessageLink to={`/admin/users/messages?recipient=${profile.id}`}>
                    Send message
                  </MessageLink>
                )}
              </AvatarActionRow>
              <MainCardGrid>
                <div>
                  <Label>Name</Label>
                  <Value>{profile.full_name}</Value>
                  <Label>Email</Label>
                  <Value>{profile.email ?? '-'}</Value>
                  <Label>Phone</Label>
                  <Value>{profile.phone ?? '-'}</Value>
                  <Label>Title</Label>
                  <Value>{profile.title ?? '-'}</Value>
                  <Label>Date of Birth</Label>
                  <Value>{formatDateValue(profile.dob)}</Value>
                  <Label>Date of Baptism</Label>
                  <Value>{formatDateValue(profile.baptism_date)}</Value>
                  <Label>Membership Status</Label>
                  <Value>{profile.membership_status ?? '-'}</Value>
                  <Label>Campus</Label>
                  <Value>{profile.campus ?? '-'}</Value>
                  <Label>Member Since</Label>
                  <Value>{profile.member_since ? new Date(profile.member_since).toLocaleDateString() : '-'}</Value>
                  <Label>Roles</Label>
                  <Value>{profile.roles?.length ? profile.roles.map(getRoleLabel).join(', ') : 'visitor'}</Value>
                </div>
                <div>
                  <Label>Household</Label>
                  <Value>{household?.name ?? '-'}</Value>
                  <Label>Household Address</Label>
                  <Value>{household?.address ?? '-'}</Value>
                  <Label>Relationship</Label>
                  <Value>{householdMember?.relationship ?? '-'}</Value>
                  <Label>Married Date</Label>
                  <Value>{householdMember?.married_date ? formatDateValue(householdMember.married_date) : '-'}</Value>
                  <Label>Primary Contact</Label>
                  <Value>{householdMember?.is_primary ? 'Yes' : 'No'}</Value>
                  <Label>Household Members</Label>
                  {householdMembers.length ? (
                    <HouseholdMemberList>
                      {householdMembers.map((member) => {
                        const displayName = member.user?.full_name || 'Unknown member';
                        const memberProfileId = member.user?.id;
                        const metaParts = [
                          member.relationship?.trim() || 'Member',
                          member.is_primary ? 'Primary contact' : null,
                          member.user?.phone?.trim() || null,
                        ].filter(Boolean);
                        return (
                          <HouseholdMemberItem key={member.id}>
                            {memberProfileId ? (
                              <HouseholdMemberLink to={`/admin/members/${memberProfileId}`}>
                                {displayName}
                              </HouseholdMemberLink>
                            ) : (
                              <HouseholdMemberName>{displayName}</HouseholdMemberName>
                            )}
                            <HouseholdMemberMeta>{metaParts.join(' • ')}</HouseholdMemberMeta>
                          </HouseholdMemberItem>
                        );
                      })}
                    </HouseholdMemberList>
                  ) : (
                    <Value>-</Value>
                  )}
                </div>
              </MainCardGrid>
            </DetailCard>

            <NotesCard>
              <NotesHeaderRow>
                <HeaderTitle style={{ fontSize: '1.2rem' }}>Notes</HeaderTitle>
                <NotesActionButton type="button" onClick={() => setShowAddNote(true)}>
                  Add Note
                </NotesActionButton>
              </NotesHeaderRow>
              <NotesBody>
                {notes.length === 0 && <p>No notes yet.</p>}
                {notes.map((note) => (
                  <div key={note.id} style={{ marginBottom: '0.75rem' }}>
                    <Value style={{ marginBottom: '0.25rem' }}>{note.note}</Value>
                    <Label>
                      {new Date(note.created_at).toLocaleString()}
                      {note.is_private ? ' • Private' : ''}
                    </Label>
                  </div>
                ))}
              </NotesBody>
            </NotesCard>
          </DetailAndNotesRow>

          <SectionGrid>
            <Card>
              <HeaderTitle style={{ fontSize: '1.2rem' }}>Status Update</HeaderTitle>
              <Label>Current Status</Label>
              <Value>{statusLabel}</Value>
              <Select value={status} onChange={(event) => setStatus(event.target.value)}>
                {STATUS_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Select>
              {status === 'inactive' && (
                <>
                  <Label style={{ marginTop: '0.75rem' }}>Inactive Reason</Label>
                  <TextArea
                    value={inactiveReason}
                    onChange={(event) => setInactiveReason(event.target.value)}
                  />
                </>
              )}
              <Button
                onClick={handleSaveStatus}
                disabled={savingStatus}
                style={{ marginTop: '0.75rem' }}
              >
                {savingStatus ? 'Saving…' : 'Save Status'}
              </Button>
            </Card>

            <Card>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem' }}>
                <HeaderTitle style={{ fontSize: '1.2rem' }}>Campus Visits</HeaderTitle>
                <HistoryButton
                  type="button"
                  onClick={() => setShowVisitHistory(true)}
                  disabled={Boolean(campusVisitError) || campusVisits.length === 0}
                  title="View visit history"
                >
                  <FiClock size={16} />
                </HistoryButton>
              </div>
              {campusVisitError && <p>{campusVisitError}</p>}
              {!campusVisitError && visitGapMessage && (
                <Label style={{ color: '#c53030', marginBottom: '0.5rem' }}>{visitGapMessage}</Label>
              )}
              {!campusVisitError && !latestCampusVisit && <p>No campus visits logged yet.</p>}
              {!campusVisitError && latestCampusVisit && (
                <div>
                  <Label>Campus</Label>
                  <Value>{latestCampusVisit.campus}</Value>
                  <Label>Visits</Label>
                  <Value>{latestCampusVisit.visit_count}</Value>
                  <Label>Last Visit</Label>
                  <Value>
                    {latestCampusVisit.last_visit_at ? new Date(latestCampusVisit.last_visit_at).toLocaleString() : '-'}
                  </Value>
                </div>
              )}
            </Card>

            <Card>
              <HeaderTitle style={{ fontSize: '1.2rem' }}>Ministries</HeaderTitle>
              {ministries.length === 0 && <p>No ministries assigned.</p>}
              {ministries.length > 0 && (
                <div>
                  {ministries.map((ministry) => (
                    <TagPill key={ministry}>{ministry}</TagPill>
                  ))}
                </div>
              )}
            </Card>

            <Card>
              <HeaderTitle style={{ fontSize: '1.2rem' }}>Status History</HeaderTitle>
              {statusHistory.length === 0 && <p>No status history yet.</p>}
              {statusHistory.map((entry) => (
                <div key={entry.id} style={{ marginBottom: '0.75rem' }}>
                  <Value style={{ marginBottom: '0.25rem' }}>
                    {entry.status} • {new Date(entry.created_at).toLocaleString()}
                  </Value>
                  {entry.reason ? <Label>{entry.reason}</Label> : null}
                </div>
              ))}
            </Card>

          </SectionGrid>
          {showVisitHistory && (
            <ModalOverlay onClick={() => setShowVisitHistory(false)}>
              <ModalCard onClick={(event) => event.stopPropagation()}>
                <ModalHeader>
                  <SectionTitle>Campus Visit History</SectionTitle>
                  <CloseButton type="button" onClick={() => setShowVisitHistory(false)}>
                    ✕
                  </CloseButton>
                </ModalHeader>
                <ModalSection>
                  {campusVisitError && <Label style={{ color: '#c53030' }}>{campusVisitError}</Label>}
                  {!campusVisitError && campusVisitHistory.length === 0 && <Label>No visits recorded yet.</Label>}
                  {!campusVisitError &&
                    campusVisitHistory.map((visit) => (
                      <div key={visit.campus} style={{ marginBottom: '0.75rem' }}>
                        <Label>Campus</Label>
                        <Value>{visit.campus}</Value>
                        <Label>Visits</Label>
                        <Value>{visit.visit_count}</Value>
                        <Label>Last Visit</Label>
                        <Value>
                          {visit.last_visit_at ? new Date(visit.last_visit_at).toLocaleString() : '-'}
                        </Value>
                      </div>
                    ))}
                </ModalSection>
              </ModalCard>
            </ModalOverlay>
          )}
          {showAddNote && (
            <ModalOverlay onClick={() => setShowAddNote(false)}>
              <ModalCard onClick={(event) => event.stopPropagation()}>
                <ModalHeader>
                  <SectionTitle>Add Note</SectionTitle>
                  <CloseButton type="button" onClick={() => setShowAddNote(false)}>
                    ✕
                  </CloseButton>
                </ModalHeader>
                <ModalSection>
                  <TextArea
                    value={noteText}
                    onChange={(event) => setNoteText(event.target.value)}
                    placeholder="Write a note about this member..."
                  />
                  <CheckboxRow>
                    <input
                      type="checkbox"
                      checked={notePrivate}
                      onChange={(event) => setNotePrivate(event.target.checked)}
                    />
                    Mark as private
                  </CheckboxRow>
                  {noteError ? <Label style={{ color: '#c53030' }}>{noteError}</Label> : null}
                  <Button
                    onClick={handleAddNote}
                    disabled={savingNote || !noteText.trim()}
                    style={{ marginTop: '0.75rem' }}
                  >
                    {savingNote ? 'Saving…' : 'Add Note'}
                  </Button>
                </ModalSection>
              </ModalCard>
            </ModalOverlay>
          )}
          {showEditProfile && (
            <ModalOverlay onClick={() => setShowEditProfile(false)}>
              <ModalCard onClick={(event) => event.stopPropagation()}>
                <ModalHeader>
                  <SectionTitle>Edit Member</SectionTitle>
                  <CloseButton type="button" onClick={() => setShowEditProfile(false)}>
                    ✕
                  </CloseButton>
                </ModalHeader>

                <ModalSection>
                  <Label>Name</Label>
                  <TextInput
                    value={editForm.full_name}
                    onChange={(event) => setEditForm({ ...editForm, full_name: event.target.value })}
                  />
                  <Label>Email</Label>
                  <TextInput
                    value={editForm.email}
                    onChange={(event) => setEditForm({ ...editForm, email: event.target.value })}
                  />
                  <Label>Phone</Label>
                  <TextInput
                    value={editForm.phone}
                    onChange={(event) => setEditForm({ ...editForm, phone: event.target.value })}
                  />
                  <Label>Title</Label>
                  <TextInput
                    value={editForm.title}
                    onChange={(event) => setEditForm({ ...editForm, title: event.target.value })}
                  />
                  <Label>Member Since</Label>
                  <TextInput
                    type="date"
                    value={editForm.member_since}
                    onChange={(event) => setEditForm({ ...editForm, member_since: event.target.value })}
                  />
                  <Label>Date of Birth</Label>
                  <TextInput
                    type="date"
                    value={editForm.dob}
                    onChange={(event) => setEditForm({ ...editForm, dob: event.target.value })}
                  />
                  <Label>Date of Baptism</Label>
                  <TextInput
                    type="date"
                    value={editForm.baptism_date}
                    onChange={(event) => setEditForm({ ...editForm, baptism_date: event.target.value })}
                  />
                  <Label>Campus</Label>
                  <Select
                    value={editForm.campus}
                    onChange={(event) => setEditForm({ ...editForm, campus: event.target.value })}
                  >
                    {CAMPUS_OPTIONS.map((option) => (
                      <option key={option.value || 'none'} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                  {profileError ? <Label style={{ color: '#c53030' }}>{profileError}</Label> : null}
                  <Button
                    onClick={handleSaveProfile}
                    disabled={savingProfile || !editForm.full_name.trim()}
                  >
                    {savingProfile ? 'Saving…' : 'Save Profile'}
                  </Button>
                </ModalSection>

                <ModalSection>
                  <AccordionToggle
                    type="button"
                    $open={householdAccordionOpen}
                    onClick={() => setHouseholdAccordionOpen((open) => !open)}
                  >
                    Edit Household {householdAccordionOpen ? '▾' : '▸'}
                  </AccordionToggle>
                  {householdAccordionOpen && (
                    <>
                      <Label>Household Name</Label>
                      <FieldRow>
                        <div>
                          <TextInput
                            value={householdSearch}
                            onChange={(event) => handleHouseholdSearchChange(event.target.value)}
                            placeholder="Search households..."
                          />
                          {householdSearch.trim().length > 0 && filteredHouseholds.length > 0 && !showCreateHousehold && (
                            <DropdownList>
                              {filteredHouseholds.map((item) => (
                                <DropdownItem key={item.id} onClick={() => handleSelectHousehold(item)}>
                                  {item.name}
                                </DropdownItem>
                              ))}
                            </DropdownList>
                          )}
                        </div>
                        <Button
                          type="button"
                          onClick={() => setShowCreateHousehold(true)}
                        >
                          Create New
                        </Button>
                      </FieldRow>
                      {showCreateHousehold && (
                        <>
                          <Label style={{ marginTop: '0.5rem' }}>New Household Name</Label>
                          <TextInput
                            value={newHouseholdName}
                            onChange={(event) => setNewHouseholdName(event.target.value)}
                          />
                          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <Button
                            type="button"
                            onClick={() => {
                              setHouseholdForm({ ...householdForm, name: newHouseholdName });
                              handleCreateHousehold(newHouseholdName);
                            }}
                            disabled={savingHousehold || !newHouseholdName.trim()}
                            style={{ marginTop: '0.5rem' }}
                          >
                            {savingHousehold ? 'Creating…' : 'Create Household'}
                          </Button>
                          <Button
                            type="button"
                            onClick={() => {
                              setShowCreateHousehold(false);
                              setNewHouseholdName('');
                            }}
                            style={{ marginTop: '0.5rem', background: 'var(--outline)', color: '#1f2937' }}
                          >
                            Cancel
                          </Button>
                          </div>
                        </>
                      )}
                      <Label>Address</Label>
                      <TextArea
                        value={householdForm.address}
                        onChange={(event) => setHouseholdForm({ ...householdForm, address: event.target.value })}
                      />
                      <Label>Relationship</Label>
                      <Select
                        value={householdForm.relationship}
                        onChange={(event) => setHouseholdForm({ ...householdForm, relationship: event.target.value })}
                      >
                        <option value="">Select relationship</option>
                        <option value="Husband">Husband</option>
                        <option value="Spouse">Spouse</option>
                        <option value="Child">Child</option>
                        <option value="Extended">Extended</option>
                        <option value="In-Law">In-Law</option>
                        <option value="Other">Other</option>
                      </Select>
                  {(householdForm.relationship === 'Husband' || householdForm.relationship === 'Spouse') && (
                    <>
                      <Label>Married Date</Label>
                      <TextInput
                        type="date"
                        value={householdForm.married_date}
                        onChange={(event) => setHouseholdForm({ ...householdForm, married_date: event.target.value })}
                      />
                    </>
                  )}
                      <CheckboxRow>
                        <input
                          type="checkbox"
                          checked={householdForm.is_primary}
                          onChange={(event) => setHouseholdForm({ ...householdForm, is_primary: event.target.checked })}
                        />
                        Primary contact for household
                      </CheckboxRow>
                      {householdError ? <Label style={{ color: '#c53030' }}>{householdError}</Label> : null}
                      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                        <Button
                          onClick={handleSaveHousehold}
                          disabled={savingHousehold || !householdForm.name.trim()}
                        >
                          {savingHousehold ? 'Saving…' : household ? 'Save Household' : 'Create Household'}
                        </Button>
                        <Button
                          type="button"
                          onClick={handleClearHousehold}
                          disabled={savingHousehold || !householdMember}
                          style={{ background: 'var(--outline)', color: '#1f2937' }}
                        >
                          Clear Household
                        </Button>
                      </div>
                    </>
                  )}
                </ModalSection>

                <ModalSection>
                  <AccordionToggle
                    type="button"
                    $open={ministriesAccordionOpen}
                    onClick={() => setMinistriesAccordionOpen((open) => !open)}
                  >
                    Edit Ministries {ministriesAccordionOpen ? '▾' : '▸'}
                  </AccordionToggle>
                  {ministriesAccordionOpen && (
                    <>
                      {volunteerCategories.length === 0 && <Label>No volunteer categories available.</Label>}
                      {volunteerCategories.map((category) => (
                        <CheckboxRow key={category.key}>
                          <input
                            type="checkbox"
                            checked={pendingMinistryKeys.includes(category.key)}
                            onChange={() => toggleMinistryKey(category.key)}
                          />
                          {category.label ?? category.key}
                        </CheckboxRow>
                      ))}
                      {ministriesError ? <Label style={{ color: '#c53030' }}>{ministriesError}</Label> : null}
                      <Button
                        type="button"
                        onClick={handleSaveMinistries}
                        disabled={savingMinistries}
                      >
                        {savingMinistries ? 'Saving…' : 'Save Ministries'}
                      </Button>
                    </>
                  )}
                </ModalSection>
              </ModalCard>
            </ModalOverlay>
          )}
        </>
      )}
      {imageModalOpen && profile?.avatar && (
        <ImageModalOverlay onClick={() => setImageModalOpen(false)}>
          <ImageModalContent onClick={(event) => event.stopPropagation()}>
            <FullImage src={profile.avatar} alt={profile.full_name} />
          </ImageModalContent>
        </ImageModalOverlay>
      )}
    </Page>
  );
};

export default MemberDetailPage;
